import { useCallback, useEffect, useRef, useState } from 'react';

import { animate, motion, useDragControls, useMotionValue } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { useUpdateElement } from '../hooks/use-update-element';
import { DateFilter, Report, ReportElement, ReportElementType } from '../types';
import { ReportElementCard } from './report-element';

export interface DroppingItem {
  type: ReportElementType;
  w: number;
  h: number;
}

const COLS = 12;
const ROW_HEIGHT = 70;
const MARGIN = 12;
const PADDING = 12;

function colWidth(containerWidth: number) {
  return (containerWidth - 2 * PADDING - (COLS - 1) * MARGIN) / COLS;
}

function toPixels(gridX: number, gridY: number, gridW: number, gridH: number, cw: number) {
  return {
    x: PADDING + gridX * (cw + MARGIN),
    y: PADDING + gridY * (ROW_HEIGHT + MARGIN),
    width: gridW * cw + (gridW - 1) * MARGIN,
    height: gridH * ROW_HEIGHT + (gridH - 1) * MARGIN,
  };
}

function toGrid(pixelX: number, pixelY: number, pixelW: number, pixelH: number, cw: number) {
  const x = Math.max(0, Math.min(COLS - 1, Math.round((pixelX - PADDING) / (cw + MARGIN))));
  const y = Math.max(0, Math.round((pixelY - PADDING) / (ROW_HEIGHT + MARGIN)));
  const w = Math.max(2, Math.min(COLS - x, Math.round((pixelW + MARGIN) / (cw + MARGIN))));
  const h = Math.max(2, Math.round((pixelH + MARGIN) / (ROW_HEIGHT + MARGIN)));
  return { x, y, w, h };
}

interface GridItemProps {
  element: ReportElement;
  reportId: string;
  dateFilter: DateFilter;
  isEditing: boolean;
  isSelected: boolean;
  onSelect: () => void;
  containerWidth: number;
  onPositionChange: (id: string, x: number, y: number, w: number, h: number) => void;
}

const GridItem = ({
  element,
  reportId,
  dateFilter,
  isEditing,
  isSelected,
  onSelect,
  containerWidth,
  onPositionChange,
}: GridItemProps) => {
  const cw = colWidth(containerWidth);
  const initial = toPixels(
    element.position.x,
    element.position.y,
    element.position.width,
    element.position.height,
    cw
  );

  // Use motion values so Framer Motion's drag offset accumulates correctly
  const motionX = useMotionValue(initial.x);
  const motionY = useMotionValue(initial.y);
  const [size, setSize] = useState({ width: initial.width, height: initial.height });

  const dragControls = useDragControls();

  // Sync from server data
  useEffect(() => {
    const p = toPixels(
      element.position.x,
      element.position.y,
      element.position.width,
      element.position.height,
      cw
    );
    motionX.set(p.x);
    motionY.set(p.y);
    setSize({ width: p.width, height: p.height });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.position.x, element.position.y, element.position.width, element.position.height, cw]);

  const handleDragEnd = useCallback(() => {
    const rawX = motionX.get();
    const rawY = motionY.get();
    const { x, y, w, h } = toGrid(rawX, rawY, size.width, size.height, cw);
    const snapped = toPixels(x, y, w, h, cw);
    // Snap to grid visually
    animate(motionX, snapped.x, { duration: 0.1, ease: 'easeOut' });
    animate(motionY, snapped.y, { duration: 0.1, ease: 'easeOut' });
    onPositionChange(element.id, x, y, w, h);
  }, [motionX, motionY, size, cw, element.id, onPositionChange]);

  // Resize via pointer events on the SE handle — separate from motion drag
  const resizing = useRef(false);
  const resizeStart = useRef({ mouseX: 0, mouseY: 0, width: 0, height: 0 });

  const handleResizePointerDown = (e: React.PointerEvent) => {
    if (!isEditing) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizing.current = true;
    resizeStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: size.width,
      height: size.height,
    };
  };

  const handleResizePointerMove = (e: React.PointerEvent) => {
    if (!resizing.current) return;
    const dx = e.clientX - resizeStart.current.mouseX;
    const dy = e.clientY - resizeStart.current.mouseY;
    setSize({
      width: Math.max(cw * 2 + MARGIN, resizeStart.current.width + dx),
      height: Math.max(ROW_HEIGHT * 2 + MARGIN, resizeStart.current.height + dy),
    });
  };

  const handleResizePointerUp = (e: React.PointerEvent) => {
    if (!resizing.current) return;
    resizing.current = false;
    const dx = e.clientX - resizeStart.current.mouseX;
    const dy = e.clientY - resizeStart.current.mouseY;
    const rawW = resizeStart.current.width + dx;
    const rawH = resizeStart.current.height + dy;
    const currentX = motionX.get();
    const currentY = motionY.get();
    const { x, y, w, h } = toGrid(currentX, currentY, rawW, rawH, cw);
    const snapped = toPixels(x, y, w, h, cw);
    setSize({ width: snapped.width, height: snapped.height });
    onPositionChange(element.id, x, y, w, h);
  };

  const isTable = element.type === ReportElementType.TABLE;

  return (
    <motion.div
      drag={isEditing}
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0}
      onDragEnd={handleDragEnd}
      onPointerMove={handleResizePointerMove}
      onPointerUp={handleResizePointerUp}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        x: motionX,
        y: motionY,
        width: size.width,
        // Tables shrink to content; configured height is the scroll threshold.
        // Other element types keep a fixed height so charts/KPIs fill their cell.
        height: isTable ? 'auto' : size.height,
        maxHeight: isTable ? size.height : undefined,
      }}
    >
      {/* Drag initiator — entire card, but not the resize handle */}
      <div
        style={{
          width: '100%',
          height: isTable ? 'auto' : '100%',
          cursor: isEditing ? 'grab' : 'default',
        }}
        onPointerDown={e => {
          if (isEditing) dragControls.start(e);
        }}
      >
        <ReportElementCard
          element={element}
          reportId={reportId}
          dateFilter={dateFilter}
          isEditing={isEditing}
          isSelected={isSelected}
          onSelect={onSelect}
          maxHeight={isTable ? size.height : undefined}
        />
      </div>

      {isEditing && (
        <div
          style={{
            position: 'absolute',
            right: 4,
            bottom: 4,
            width: 12,
            height: 12,
            cursor: 'se-resize',
            zIndex: 1,
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 2px,
              hsl(var(--muted-foreground) / 0.4) 2px,
              hsl(var(--muted-foreground) / 0.4) 3px
            )`,
          }}
          onPointerDown={handleResizePointerDown}
        />
      )}
    </motion.div>
  );
};

interface CanvasGridProps {
  report: Report;
  dateFilter: DateFilter;
  isEditing: boolean;
  droppingItem: DroppingItem | null;
  onElementDropped: (type: string, x: number, y: number, w: number, h: number) => void;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
}

export const CanvasGrid = ({
  report,
  dateFilter,
  isEditing,
  droppingItem,
  onElementDropped,
  selectedElementId,
  onSelectElement,
}: CanvasGridProps) => {
  const { t } = useTranslation('authenticated.reports');
  const { mutate: updateElement } = useUpdateElement();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    setContainerWidth(node.offsetWidth);
    const observer = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const allElements = report.elements;

  const handlePositionChange = useCallback(
    (elementId: string, x: number, y: number, w: number, h: number) => {
      updateElement({
        reportId: report.id,
        elementId,
        body: { position: { x, y, width: w, height: h } },
      });
    },
    [report.id, updateElement]
  );

  const cw = containerWidth > 0 ? colWidth(containerWidth) : 0;

  const canvasHeight =
    allElements.length === 0
      ? 500
      : Math.max(
          500,
          ...allElements.map(
            el => PADDING + (el.position.y + el.position.height) * (ROW_HEIGHT + MARGIN) + PADDING
          )
        );

  const isEmpty = allElements.length === 0;

  const handleDragOver = (e: React.DragEvent) => {
    if (!isEditing || !droppingItem) return;
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!isEditing || !droppingItem || !containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const { x, y } = toGrid(e.clientX - rect.left, e.clientY - rect.top, 0, 0, cw);
    onElementDropped(droppingItem.type, x, y, droppingItem.w, droppingItem.h);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height: canvasHeight }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {containerWidth > 0 &&
        allElements.map(el => (
          <GridItem
            key={el.id}
            element={el}
            reportId={report.id}
            dateFilter={dateFilter}
            isEditing={isEditing}
            isSelected={selectedElementId === el.id}
            onSelect={() => onSelectElement(el.id === selectedElementId ? null : el.id)}
            containerWidth={containerWidth}
            onPositionChange={handlePositionChange}
          />
        ))}

      {isEmpty && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25">
          <p className="text-sm text-muted-foreground">
            {isEditing ? t('builder.dragElementsHere') : t('builder.noElements')}
          </p>
        </div>
      )}
    </div>
  );
};
